import { Button, H1, Input } from '@/design-system'
import { isValidPhoneNumber } from 'libphonenumber-js/min'
import { useRef, useState } from 'react'
import RegistrationForm from './RegistrationForm'
import VerifyForm from './VerifyForm'

declare global {
  interface Window {
    grecaptcha: any
  }
}

const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITEKEY

const LoginForm = ({
  onLogin,
  onCancel = null,
  profileFields = { email: {}, cnp: {} },
  embedded = false,
}) => {
  const input = useRef<any>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [verificationId, setVerificationId] = useState(null)
  const [registering, setRegistering] = useState(false)
  const [globalError, setGlobalError] = useState(null)

  const login = async () => {
    setSubmitting(true)
    const formData = { phone_number: input.current?.value }
    if (!isValidPhoneNumber(input.current?.value, 'RO')) {
      setError({ message: 'Număr de telefon mobil invalid' })
      setSubmitting(false)
      return
    }

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(SITE_KEY, { action: 'submit' })
        .then(async (token) => {
          formData['recaptchaResponse'] = token

          const loginRes = await fetch('/api/auth/login', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(formData),
          })

          if (loginRes.status == 404) {
            setSubmitting(false)
            setRegistering(true)
          }

          const loginJson = await loginRes.json()
          setSubmitting(false)
          if (loginJson.errors) {
            for (const [field, err] of Object.entries(loginJson.errors)) {
              setError({ message: err })
              input.current.focus()
            }
          } else {
            if (loginJson.error) {
              setGlobalError(loginJson.error)
            } else {
              setVerificationId(loginJson.verification_id)
            }
          }
        })
        .catch((error) => {
          console.log('error', error?.message)
        })
    })
  }

  if (registering) {
    return (
      <RegistrationForm
        embedded={embedded}
        phoneNr={input.current?.value}
        cancelCallback={() => {
          onCancel ? onCancel() : setRegistering(false)
        }}
        completeCallback={onLogin}
        profileFields={profileFields}
      />
    )
  }

  if (verificationId) {
    return (
      <VerifyForm
        verificationId={verificationId}
        verifyDoneCallback={onLogin}
      />
    )
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        login()
      }}
    >
      {!embedded && <H1>Intră în cont</H1>}

      {globalError && (
        <div className="bg-red-100 p-3 mb-2 text-red-800 rounded-md">
          {globalError}
        </div>
      )}

      <div className="px-4 py-5 mb-6 border-t-0 sm:border-l-4 bg-primary-bg border-primary sm:p-6">
        <div className="grid grid-cols-12 gap-6">
          <Input
            type="tel"
            label="Număr telefon mobil"
            name="phone_number"
            className="col-span-12 sm:col-span-6"
            ref={input}
            error={error}
            autoFocus={embedded ? false : true}
            required
            onKeyDown={() => {
              setError(null)
            }}
          />
        </div>
      </div>
      <div className="text-slate-400 text-xs -mt-4 mb-6">
        This site is protected by reCAPTCHA and the Google{' '}
        <a
          href="https://policies.google.com/privacy"
          className="hover:text-primary"
          target="_blank"
        >
          Privacy Policy
        </a>{' '}
        and{' '}
        <a
          href="https://policies.google.com/terms"
          className="hover:text-primary"
          target="_blank"
        >
          Terms of Service
        </a>{' '}
        apply.
      </div>
      <div className="items-center space-y-2 sm:flex sm:space-y-0 sm:space-x-4">
        <Button
          primary
          type="button"
          disabled={submitting}
          onClick={login}
          className="inline-flex items-center"
        >
          {submitting && (
            <svg
              className="w-5 h-5 mr-2 text-white animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx={12}
                cy={12}
                r={10}
                stroke="currentColor"
                strokeWidth={4}
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          )}
          Creează sau intră în cont
        </Button>
        {onCancel && (
          <>
            <div>sau</div>
            <Button type="button" onClick={onCancel}>
              Renunță
            </Button>
          </>
        )}
      </div>
    </form>
  )
}

export default LoginForm
